// Standalone from the ERP's src/api.jsx on purpose — that file globally
// overrides window.fetch to redirect to "/login" and clear the
// internal staff's localStorage keys on any 401, which would have kept
// this portal implicitly coupled to internal-app behavior. This portal
// uses its own axios instances below with its own token handling, so no
// such override is needed here.
import axios from "axios";
import { getSupplierAuthData, clearSupplierAuthData } from "./Utils/supplierAuth";
import { getAdminAuthData, clearAdminAuthData } from "./Utils/adminAuth";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Two separate axios instances, matching the backend's own token
// isolation (SUPPLIER_JWT_SECRET vs ADMIN_JWT_SECRET) — a supplier page
// must never accidentally send an admin token or vice versa, so each
// instance only ever reads its own localStorage keys via the request
// interceptor (read fresh per-request, not captured at creation time,
// so it picks up the token as soon as login sets it).
//
// Note: the backend expects the raw token in the Authorization header,
// NOT "Bearer <token>" — see jpc-supplier-backend/middleware/procurement/
// {supplierAuth,jpcUserAuth}.js, which do `req.headers['authorization']`
// directly through jwt.verify() with no prefix stripping.
export const supplierApi = axios.create({ baseURL: API_BASE_URL });
supplierApi.interceptors.request.use((config) => {
  const { token } = getSupplierAuthData();
  if (token) config.headers.Authorization = token;
  return config;
});

export const adminApi = axios.create({ baseURL: API_BASE_URL });
adminApi.interceptors.request.use((config) => {
  const { token } = getAdminAuthData();
  if (token) config.headers.Authorization = token;
  return config;
});

// Auth-failure messages jpcUserAuth.js/supplierAuth.js throw when there's
// no usable session at all (expired/invalid/missing token) — as opposed to
// a role-check 403 ("You are not authorised to perform this action"),
// which means the user IS logged in, just lacks permission for that
// route, and must NOT force a logout/redirect.
const SESSION_INVALID_MESSAGES = [
  "No token provided",
  "Invalid token",
  "Session expired, please login again",
];

adminApi.interceptors.response.use(
  (res) => res,
  (error) => {
    const message = error.response?.data?.message;
    const onLoginPage = window.location.pathname.startsWith("/login");
    if (SESSION_INVALID_MESSAGES.includes(message) && !onLoginPage) {
      clearAdminAuthData();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// supplierApi previously had no response interceptor at all, so an
// expired/invalid supplier token surfaced as a raw uncaught 401
// AxiosError in the CRA dev overlay instead of redirecting to login,
// same class of bug the adminApi interceptor above already handles.
supplierApi.interceptors.response.use(
  (res) => res,
  (error) => {
    const message = error.response?.data?.message;
    const onLoginPage = window.location.pathname.startsWith("/login");
    if (SESSION_INVALID_MESSAGES.includes(message) && !onLoginPage) {
      clearSupplierAuthData();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);
